import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TOOLS } from '@/registry/tools';
import { selectActiveToolId, usePrefs } from '@/store/prefs';
import type { ToolDefinition } from '@/types/tool';
import { cn } from '@/lib/cn';

type Orientation = 'vertical' | 'horizontal';

/**
 * Dock layout constants. The pointer math, hover-index detection, and
 * magnification curve all share these — change one place, the rest follows.
 */
const SLOT = 36;
const GAP = 2; // matches `gap-0.5` in the dock container
const PAD = 8; // matches `py-2` / `px-2` in the dock container
const STRIDE = SLOT + GAP;
const ICON_INNER = 22;
const MAX_SCALE = 1.7;
const INFLUENCE = 96;

function computeScale(distance: number): number {
  const d = Math.abs(distance);
  if (d > INFLUENCE) return 1;
  const t = Math.cos((d / INFLUENCE) * (Math.PI / 2));
  return 1 + (MAX_SCALE - 1) * t;
}

interface DockProps {
  orientation: Orientation;
}

export function Dock({ orientation }: DockProps) {
  const activeToolId = usePrefs(selectActiveToolId);
  const openToolStore = usePrefs((s) => s.openTool);
  const pushRecent = usePrefs((s) => s.pushRecent);
  const favorites = usePrefs((s) => s.favorites);
  const dockTools = usePrefs((s) => s.dockTools);

  // Resolve the tool list the user pinned for the dock. `null` = show all.
  const visibleTools = useMemo<ToolDefinition[]>(() => {
    if (!dockTools) return TOOLS;
    const allowed = new Set(dockTools);
    return TOOLS.filter((t) => allowed.has(t.id));
  }, [dockTools]);

  /** Pointer position in dock *content* space (visible pointer + scroll). */
  const [pointer, setPointer] = useState<number | null>(null);
  const buttonsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Stable per-index ref setters so React doesn't churn null↔element each
  // render. Re-derived when the visible-tools count changes.
  const setButtonRef = useMemo(
    () =>
      visibleTools.map((_, i) => (el: HTMLButtonElement | null) => {
        buttonsRef.current[i] = el;
      }),
    [visibleTools.length],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      const scroll = orientation === 'vertical' ? el.scrollTop : el.scrollLeft;
      const pos =
        orientation === 'vertical'
          ? e.clientY - rect.top + scroll
          : e.clientX - rect.left + scroll;
      setPointer(pos);
    },
    [orientation],
  );

  const handleClick = (id: string) => {
    openToolStore(id);
    pushRecent(id);
  };

  // Which slot is the pointer over? Pure derived value, no extra state.
  const hoverIdx = useMemo<number | null>(() => {
    if (pointer === null) return null;
    const idx = Math.floor((pointer - PAD) / STRIDE);
    if (idx < 0 || idx >= visibleTools.length) return null;
    return idx;
  }, [pointer, visibleTools.length]);

  const dockBody = (
    <div
      ref={scrollRef}
      role="toolbar"
      aria-label="Tool dock"
      onPointerMove={handlePointerMove}
      onPointerLeave={() => setPointer(null)}
      className={cn(
        'no-scrollbar flex shrink-0 items-center gap-0.5 bg-background/95 backdrop-blur',
        orientation === 'vertical'
          ? 'h-full w-10 flex-col overflow-y-auto overflow-x-hidden border-l border-border py-2'
          : 'h-10 w-full flex-row overflow-x-auto overflow-y-hidden border-t border-border px-2',
      )}
    >
      {visibleTools.length === 0 ? (
        <div
          className={cn(
            'flex h-full items-center justify-center text-2xs italic text-muted-foreground',
            orientation === 'vertical' ? 'w-full' : 'w-full',
          )}
        >
          {orientation === 'vertical' ? null : 'No tools — open Settings to pick'}
        </div>
      ) : (
        visibleTools.map((tool, i) => {
          const slotCenter = PAD + i * STRIDE + SLOT / 2;
          const scale =
            pointer !== null ? computeScale(slotCenter - pointer) : 1;
          return (
            <DockButton
              key={tool.id}
              buttonRef={setButtonRef[i]!}
              tool={tool}
              orientation={orientation}
              scale={scale}
              active={tool.id === activeToolId}
              favorite={favorites.includes(tool.id)}
              hovering={pointer !== null}
              onClick={() => handleClick(tool.id)}
            />
          );
        })
      )}
    </div>
  );

  return (
    <>
      {dockBody}
      {hoverIdx !== null ? (
        <DockLabel
          target={buttonsRef.current[hoverIdx] ?? null}
          text={visibleTools[hoverIdx]!.name}
          orientation={orientation}
        />
      ) : null}
    </>
  );
}

interface DockButtonProps {
  tool: ToolDefinition;
  orientation: Orientation;
  scale: number;
  active: boolean;
  favorite: boolean;
  hovering: boolean;
  onClick: () => void;
  buttonRef: (el: HTMLButtonElement | null) => void;
}

function DockButton({
  tool,
  orientation,
  scale,
  active,
  favorite,
  hovering,
  onClick,
  buttonRef,
}: DockButtonProps) {
  const Icon = tool.icon;
  const transition = hovering
    ? 'none'
    : 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)';

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      aria-label={tool.name}
      title={tool.name}
      aria-current={active ? 'page' : undefined}
      style={{ width: SLOT, height: SLOT }}
      className="relative flex shrink-0 items-center justify-center focus-visible:outline-none"
    >
      <span
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          transition,
          width: ICON_INNER,
          height: ICON_INNER,
        }}
        className={cn(
          'flex items-center justify-center rounded-md ring-1 ring-inset transition-colors',
          active
            ? 'bg-primary/15 text-primary ring-primary/40'
            : 'bg-secondary text-secondary-foreground ring-transparent hover:bg-accent',
        )}
      >
        <Icon className="h-3.5 w-3.5" strokeWidth={2} />
        {favorite ? (
          <span className="pointer-events-none absolute right-0.5 top-0.5 h-1 w-1 rounded-full bg-warning" />
        ) : null}
      </span>
      {active ? (
        <span
          className={cn(
            'pointer-events-none absolute rounded-full bg-primary',
            orientation === 'vertical'
              ? 'left-0.5 h-1 w-1'
              : 'bottom-0.5 h-1 w-1',
          )}
        />
      ) : null}
    </button>
  );
}

function DockLabel({
  target,
  text,
  orientation,
}: {
  target: HTMLButtonElement | null;
  text: string;
  orientation: Orientation;
}) {
  if (!target) return null;
  const rect = target.getBoundingClientRect();

  const style: React.CSSProperties =
    orientation === 'vertical'
      ? {
          position: 'fixed',
          top: rect.top + rect.height / 2,
          left: rect.left - 10,
          transform: 'translate(-100%, -50%)',
        }
      : {
          position: 'fixed',
          top: rect.top - 10,
          left: rect.left + rect.width / 2,
          transform: 'translate(-50%, -100%)',
        };

  return createPortal(
    <div
      role="tooltip"
      style={style}
      className="pointer-events-none z-[9999] whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-2xs font-medium text-popover-foreground shadow-md"
    >
      {text}
    </div>,
    document.body,
  );
}
